// Event batching system to reduce Firestore writes
// Buffers events and flushes periodically or on threshold

import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface Event {
  type: string;
  userId: string;
  data: any;
  timestamp: number;
}

const eventBuffer: Event[] = [];
const FLUSH_INTERVAL = 60000; // 60 seconds
const FLUSH_THRESHOLD = 10; // Max events before auto-flush
let flushTimer: NodeJS.Timeout | null = null;

function scheduleFlush() {
  if (flushTimer) return;
  
  flushTimer = setTimeout(() => {
    flush();
    flushTimer = null;
  }, FLUSH_INTERVAL);
}

export function logEvent(type: string, userId: string, data: any) {
  // Only log meaningful events
  const meaningfulEvents = [
    'lesson_completed',
    'course_purchased',
    'certificate_issued'
  ];
  
  if (!meaningfulEvents.includes(type)) {
    return; // Skip non-meaningful events
  }
  
  eventBuffer.push({
    type,
    userId,
    data,
    timestamp: Date.now()
  });
  
  // Auto-flush if threshold reached
  if (eventBuffer.length >= FLUSH_THRESHOLD) {
    flush();
  } else {
    scheduleFlush();
  }
}

export async function flush() {
  if (eventBuffer.length === 0) return;
  
  const eventsToFlush = [...eventBuffer];
  eventBuffer.length = 0; // Clear buffer
  
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  
  try {
    // Batch write to Firestore
    const promises = eventsToFlush.map(event =>
      addDoc(collection(db, 'analytics_events'), {
        ...event,
        timestamp: Timestamp.fromMillis(event.timestamp)
      })
    );
    
    await Promise.all(promises);
  } catch (error) {
    console.error('Failed to flush events:', error);
    // Re-add failed events to buffer for retry
    eventBuffer.push(...eventsToFlush);
  }
}

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    flush();
  });
  
  // Also flush periodically
  setInterval(flush, FLUSH_INTERVAL);
}
